/**
 * Reads UTM params from a JSON-encoded localStorage entry and returns them as a flat object
 * with mapped output keys.
 *
 * Output keys are determined by one of two mutually exclusive modes:
 *   - Prefix mode (default): each UTM key is prefixed with `keyPrefix`
 *     e.g. keyPrefix = 'last_touch_first_party_' → { last_touch_first_party_utm_campaign: '...' }
 *   - Map mode: `keyMap` provides an explicit { utmKey → outputKey } mapping, which allows
 *     arbitrary output key names regardless of casing or structure.
 *     Keys absent from `keyMap` (or mapped to a falsy value) are skipped.
 *
 * @param storageKey - The localStorage key holding the JSON UTM object.
 * @param keyPrefix - Prefix applied to each UTM key in prefix mode. Pass null when using keyMap.
 * @param keyMap - Optional explicit mapping of UTM keys to output keys.
 *   When provided, keyMap takes precedence over keyPrefix.
 *   Example: { utm_campaign: 'First_touch_utm_campaign', utm_id: 'first_touch_utm_id' }
 * @returns Flat object of mapped UTM values. Missing or unparseable data returns empty strings.
 */
const getUtmFromLocalStorage = (
  storageKey: string,
  keyPrefix: string | null,
  keyMap?: Record<string, string>
): Record<string, string> => {
  const utmKeys = ['utm_campaign', 'utm_content', 'utm_medium', 'utm_term', 'utm_source', 'utm_id']
  const result: Record<string, string> = {}

  try {
    const storedData = localStorage.getItem(storageKey)
    let utmData: Record<string, string> = {}

    if (storedData !== null) {
      try {
        utmData = JSON.parse(storedData)
      } catch (parseError) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[getUtmFromLocalStorage] Failed to parse stored UTM data')
        }
      }
    }

    for (let i = 0; i < utmKeys.length; i++) {
      const key = utmKeys[i]
      const outputKey = keyMap ? keyMap[key] : (keyPrefix ?? '') + key
      if (outputKey) {
        result[outputKey] = utmData[key] || ''
      }
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getUtmFromLocalStorage] localStorage not available for UTM tracking')
    }
  }

  return result
}

/**
 * Reads attribution data from browser storage (localStorage, sessionStorage, cookies)
 * and browser APIs. These values are sent to Mixpanel for attribution tracking.
 *
 * @returns Attribution data with backend-expected field names
 */
export const getAttributionData = (): Record<string, string> => {
  // IMPORTANT: Field names must match backend UtmData class exactly
  // Pay attention to casing: first_touch_utm_id (lowercase 'f'), Session-Landingpage (hyphen)
  const attribution: Record<string, string> = {
    First_touch_utm_campaign: '',
    First_touch_utm_content: '',
    First_touch_utm_medium: '',
    First_touch_utm_term: '',
    First_touch_utm_source: '',
    first_touch_utm_id: '', // NOTE: lowercase 'f'
    last_touch_first_party_utm_campaign: '',
    last_touch_first_party_utm_content: '',
    last_touch_first_party_utm_id: '',
    last_touch_first_party_utm_medium: '',
    last_touch_first_party_utm_source: '',
    last_touch_first_party_utm_term: '',
    last_touch_third_party_utm_campaign: '',
    last_touch_third_party_utm_content: '',
    last_touch_third_party_utm_id: '',
    last_touch_third_party_utm_medium: '',
    last_touch_third_party_utm_source: '',
    last_touch_third_party_utm_term: '',
    Referrer_pagetype: '',
    Referrer_contentGroup3: '',
    Landing_page: '',
    Initial_referrer: '',
    Device: '',
    'Session-Landingpage': '', // NOTE: hyphen, not underscore
    Referrer: ''
  }

  const firstTouchUtms = getUtmFromLocalStorage('firstTouchUtmParams', null, {
    utm_campaign: 'First_touch_utm_campaign',
    utm_content: 'First_touch_utm_content',
    utm_medium: 'First_touch_utm_medium',
    utm_term: 'First_touch_utm_term',
    utm_source: 'First_touch_utm_source',
    utm_id: 'first_touch_utm_id' // lowercase 'f'
  })
  Object.assign(attribution, firstTouchUtms)

  const lastTouchThirdPartyUtms = getUtmFromLocalStorage(
    'lastTouchThirdPartyUtmParams', 'last_touch_third_party_')
  Object.assign(attribution, lastTouchThirdPartyUtms)

  const lastTouchFirstPartyUtms = getUtmFromLocalStorage(
    'lastTouchFirstPartyUtmParams', 'last_touch_first_party_')
  Object.assign(attribution, lastTouchFirstPartyUtms)

  try {
    // Read page metrics from sessionStorage
    const pageData = sessionStorage.getItem('pageMetricProps')
    if (pageData) {
      const metrics = JSON.parse(pageData)
      attribution.Referrer_pagetype = metrics?.referrer?.pageType || ''
      attribution.Referrer_contentGroup3 = metrics?.referrer?.contentGroup3 || ''
    }
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getAttributionData] Error reading pageMetricProps:', e)
    }
  }

  try {
    // Read session landing page from sessionStorage
    const sessionLandingPage = sessionStorage.getItem('sessionLandingPage')
    attribution['Session-Landingpage'] = sessionLandingPage || '' // hyphen, not underscore
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getAttributionData] Error reading sessionLandingPage:', e)
    }
  }

  try {
    // Read landing page from cookie
    const landingPageMatch = document.cookie.match(/(^| )landingPage=([^;]+)/)
    attribution.Landing_page = (landingPageMatch && landingPageMatch.length > 2)
      ? landingPageMatch[2]
      : ''
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getAttributionData] Error reading landingPage cookie:', e)
    }
  }

  try {
    // Capture referrer from browser API
    attribution.Referrer = document.referrer || ''

    // Initial referrer (sticky first-ever referrer)
    // Three-layer fallback: localStorage → current referrer → empty
    let initialReferrer = ''

    // Layer 1: Check our own localStorage (most reliable)
    try {
      const stored = localStorage.getItem('sa_initial_referrer')
      if (stored) {
        initialReferrer = stored
      }
    } catch (e) {
      // localStorage may be unavailable (private browsing)
    }

    // Layer 2: Fallback to current document.referrer (last resort, also persist for future)
    if (!initialReferrer) {
      const currentReferrer = document.referrer || ''
      if (currentReferrer) {
        initialReferrer = currentReferrer
        // Store for future visits to make it sticky
        try {
          localStorage.setItem('sa_initial_referrer', initialReferrer)
        } catch (e) {
          // localStorage write may fail (private browsing)
        }
      }
    }

    attribution.Initial_referrer = initialReferrer

    // Device detection
    const userAgent = window.navigator.userAgent
    let device = ''
    if (/Windows Phone/i.test(userAgent) || /WPDesktop/.test(userAgent)) {
      device = 'Windows Phone'
    } else if (/iPad/.test(userAgent)) {
      device = 'iPad'
    } else if (/iPod/.test(userAgent)) {
      device = 'iPod Touch'
    } else if (/iPhone/.test(userAgent)) {
      device = 'iPhone'
    } else if (/(BlackBerry|PlayBook|BB10)/i.test(userAgent)) {
      device = 'BlackBerry'
    } else if (/Android/.test(userAgent)) {
      device = 'Android'
    }
    attribution.Device = device
  } catch (e) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('[getAttributionData] Error reading browser properties:', e)
    }
  }

  return attribution
}
